<template>
  <div ref="containerRef" class="adaptive-popup-apps-buttons contents">
    <!-- Visible apps as individual buttons -->
    <template v-for="(app, index) in visibleApps" :key="app.id">
      <CButton
        v-tooltip="app.app_metadata?.display_name || app.name"
        :circle="isCircle"
        :class="[
          buttonClass,
          {
            'ml-1': index > 0,
          },
        ]"
        secondary
        :size="buttonSize"
        strong
        @click="onAppClick(app)"
      >
        <template #icon>
          <CIcon
            :fa-type="getAppIcon(app).includes('fa-kit') ? 'fa-kit' : 'far'"
            :icon="getAppIcon(app)"
            :size="iconSize"
          />
        </template>
      </CButton>
    </template>

    <!-- Overflow popover for hidden apps -->
    <CPopover
      v-if="shouldShowPopover"
      :placement="placement"
      :showArrow="true"
      :theme-overrides="{
        color: themeVars.base,
      }"
      to="body"
      trigger="click"
    >
      <template #trigger>
        <CButton
          v-tooltip="t('canvasUI.canvasBuilder.components.categories.moreApps')"
          :circle="isCircle"
          :class="[buttonClass, { 'ml-1': visibleApps.length > 0 }]"
          secondary
          :size="buttonSize"
          strong
        >
          <template #icon>
            <CIcon fa-type="fas" icon="ellipsis" :size="iconSize" />
          </template>
        </CButton>
      </template>

      <AppGrid :apps="hiddenApps" :get-app-icon="getAppIcon" @click="onAppClick" />
    </CPopover>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useResizeObserver, useDebounceFn } from '@vueuse/core'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CPopover from '@lib/components/CPopover/CPopover.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import AppGrid from '@canvas-builder/components/container/ControlBar/AppGrid.vue'
import { usePopupApps } from '@canvas-builder/composables/usePopupApps'
import useCanvas from '@canvas-builder/composables/useCanvas'
import tooltipDirective from '@lib/directives/tooltip'

interface Props {
  placement?:
    | 'left'
    | 'right'
    | 'top'
    | 'bottom'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end'
    | 'left-start'
    | 'left-end'
    | 'right-start'
    | 'right-end'
  buttonClass?: string
  buttonSize?: 'tiny' | 'small' | 'medium' | 'large'
  iconSize?: string | number
  isCircle?: boolean
}

withDefaults(defineProps<Props>(), {
  placement: 'left',
  buttonClass: 'h-11 w-11',
  buttonSize: 'medium',
  iconSize: '18',
  isCircle: false,
})

const { t } = useI18n()
const themeVars = useThemeVars()
const vTooltip = tooltipDirective
const { popupApps, onAppClick, getAppIcon } = usePopupApps()
const { isEditMode } = useCanvas()

// Container ref for measuring
const containerRef = ref<HTMLElement | null>(null)

// State for visibility calculations
const availableHeight = ref(0)
const maxVisibleApps = ref(0)
const isCalculating = ref(false)

// Constants for button dimensions (based on control-bar__button class: h-11 w-11)
const BUTTON_HEIGHT = 44 // 11 * 4px (Tailwind h-11)
const BUTTON_GAP = 8 // gap-2 in Tailwind (2 * 4px)
const MIN_BUFFER = 16 // Minimum buffer to prevent overlap

// Find the navigation wrapper and sections
const findNavigationElements = () => {
  if (!containerRef.value) return null

  // Find the navigation wrapper by traversing up the DOM
  let element = containerRef.value.parentElement
  while (element && !element.classList.contains('navigation-wrapper')) {
    element = element.parentElement
  }

  if (!element) return null

  const topSection = element.querySelector('.navigation-wrapper__top-section')
  const bottomSection = element.querySelector('.navigation-wrapper__bottom-section')
  const middleSection = element.querySelector('.navigation-wrapper__middle-section')

  return {
    wrapper: element,
    topSection: topSection as HTMLElement,
    bottomSection: bottomSection as HTMLElement,
    middleSection: middleSection as HTMLElement,
  }
}

// Calculate the true content height by measuring all child elements
const calculateSectionContentHeight = (
  section: HTMLElement | null,
  sectionName: string,
  skipContainer?: HTMLElement,
) => {
  if (!section) return 0

  let totalContentHeight = 0
  const children = Array.from(section.children) as HTMLElement[]

  if (children.length === 0) {
    return 0
  }

  for (const child of children) {
    // Skip our own container when calculating middle section to avoid counting our apps
    if (skipContainer && (child === skipContainer || child.contains(skipContainer))) {
      continue
    }

    // Get the complete height of each child including margins
    const childRect = child.getBoundingClientRect()
    const childStyles = getComputedStyle(child)
    const marginTop = parseFloat(childStyles.marginTop || '0')
    const marginBottom = parseFloat(childStyles.marginBottom || '0')

    const elementHeight = childRect.height + marginTop + marginBottom
    totalContentHeight += elementHeight
  }

  // Add gaps between elements (sections use gap-2 = 8px)
  const numberOfGaps = Math.max(0, children.length - 1 - (skipContainer ? 1 : 0))
  if (numberOfGaps > 0) {
    totalContentHeight += numberOfGaps * BUTTON_GAP
  }

  return totalContentHeight
}

// Calculate the height used by existing middle section content (excluding our popup apps)
const calculateExistingMiddleContentHeight = (middleSection: HTMLElement | null) => {
  const height = calculateSectionContentHeight(middleSection, 'MIDDLE', containerRef.value || undefined)

  // Add gap between existing content and our popup apps if there's existing content
  if (height > 0) {
    return height + BUTTON_GAP
  }

  return height
}

// Calculate available height for middle section
const calculateAvailableHeight = async () => {
  try {
    const elements = findNavigationElements()
    if (!elements) {
      availableHeight.value = 0
      return
    }

    const wrapperRect = elements.wrapper.getBoundingClientRect()

    // Calculate true content heights by measuring child elements
    const topHeight = calculateSectionContentHeight(elements.topSection, 'TOP')
    const bottomHeight = calculateSectionContentHeight(elements.bottomSection, 'BOTTOM')
    const existingMiddleContentHeight = calculateExistingMiddleContentHeight(elements.middleSection)

    // Calculate available height: total wrapper height minus all used space minus buffer
    const calculatedHeight = wrapperRect.height - topHeight - bottomHeight - existingMiddleContentHeight - MIN_BUFFER

    availableHeight.value = Math.max(0, calculatedHeight)
  } catch (error) {
    availableHeight.value = 0
  }
}

// Calculate maximum number of apps that can fit
const calculateMaxVisibleApps = () => {
  if (popupApps.value.length === 0) {
    maxVisibleApps.value = 0
    return
  }

  // If calculated height is negative or very small, show no individual apps
  if (availableHeight.value <= 0) {
    maxVisibleApps.value = 0
    return
  }

  // Calculate space needed for one app button
  const spacePerApp = BUTTON_HEIGHT + BUTTON_GAP
  const overflowButtonSpace = BUTTON_HEIGHT + BUTTON_GAP // Space for "..." button + gap

  // If we have only one app, we can show it directly without overflow button
  if (popupApps.value.length === 1) {
    if (availableHeight.value >= BUTTON_HEIGHT) {
      maxVisibleApps.value = 1
      return
    } else {
      maxVisibleApps.value = 0
      return
    }
  }

  // For multiple apps, we need space for overflow button + at least one individual app
  // Minimum space needed: overflow button (44px) + gap (8px) + first app (44px) = 96px
  const minimumSpaceForInlineApps = overflowButtonSpace + BUTTON_HEIGHT

  if (availableHeight.value < minimumSpaceForInlineApps) {
    maxVisibleApps.value = 0
    return
  }

  // Calculate how many apps can fit alongside the overflow button
  // Available space minus overflow button space, divided by space per app
  const spaceForIndividualApps = availableHeight.value - overflowButtonSpace
  const maxApps = Math.floor(spaceForIndividualApps / spacePerApp)

  // If we can fit ALL apps individually, we don't need the overflow button
  if (maxApps >= popupApps.value.length) {
    const spaceForAllApps = popupApps.value.length * spacePerApp - BUTTON_GAP // Subtract last gap
    if (spaceForAllApps <= availableHeight.value) {
      maxVisibleApps.value = popupApps.value.length
      return
    }
  }

  // Show as many apps as fit, with overflow button for the rest
  maxVisibleApps.value = Math.min(maxApps, popupApps.value.length - 1) // -1 because we need at least one in overflow
}

// Recalculate visibility when dimensions change
const recalculate = async () => {
  if (isCalculating.value) return

  isCalculating.value = true
  try {
    await calculateAvailableHeight()
    calculateMaxVisibleApps()
  } finally {
    isCalculating.value = false
  }
}

watch(
  [() => popupApps.value.length, isEditMode],
  () => {
    // Use timeout to allow DOM updates before recalculating
    setTimeout(() => {
      recalculate()
    }, 150)
  },
  { immediate: true },
)

// Debounced recalculation to prevent excessive calls
const debouncedRecalculate = useDebounceFn(recalculate, 100)

// Set up resize observer for the container
const { stop: stopResizeObserver } = useResizeObserver(containerRef, () => {
  debouncedRecalculate()
})

// Window resize handler for viewport changes
const handleWindowResize = () => {
  debouncedRecalculate()
}

onMounted(() => {
  window.addEventListener('resize', handleWindowResize)
  // Initial calculation after mount
  nextTick(() => recalculate())
})

onUnmounted(() => {
  window.removeEventListener('resize', handleWindowResize)
  stopResizeObserver()
})

// Computed properties for component use
const visibleApps = computed(() => popupApps.value.slice(0, maxVisibleApps.value))
const hiddenApps = computed(() => {
  // When maxVisibleApps is 0, all apps go in the popover
  if (maxVisibleApps.value === 0) {
    return popupApps.value
  }
  return popupApps.value.slice(maxVisibleApps.value)
})
const shouldShowPopover = computed(() => {
  // Show popover if there are any popup apps and either:
  // 1. There are hidden apps (normal overflow case)
  // 2. No apps are visible because space is too small (all apps in popover)
  return popupApps.value.length > 0 && (hiddenApps.value.length > 0 || maxVisibleApps.value === 0)
})
</script>
